'use strict';

var dateFns = require('date-fns');
var differenceInCalendarDays = require('date-fns/differenceInCalendarDays');
var eachDayOfInterval = require('date-fns/eachDayOfInterval');
var endOfWeek = require('date-fns/endOfWeek');
var isAfter = require('date-fns/isAfter');
var isBefore = require('date-fns/isBefore');
var isEqual = require('date-fns/isEqual');
var isMonday = require('date-fns/isMonday');
var isSunday = require('date-fns/isSunday');
var isWithinInterval = require('date-fns/isWithinInterval');
var startOfDay = require('date-fns/startOfDay');
var startOfWeek = require('date-fns/startOfWeek');
var React = require('react');
var reactUtils = require('@sravni/react-utils');
var Space = require('../../Space/src/Space.js');
var Calendar_module = require('../styles/Calendar.module.less.js');
var Day = require('./Day.js');

function _interopNamespaceDefault(e) {
    var n = Object.create(null);
    if (e) {
        Object.keys(e).forEach(function (k) {
            if (k !== 'default') {
                var d = Object.getOwnPropertyDescriptor(e, k);
                Object.defineProperty(n, k, d.get ? d : {
                    enumerable: true,
                    get: function () { return e[k]; }
                });
            }
        });
    }
    n.default = e;
    return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespaceDefault(React);

// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable complexity */
const Week = (props) => {
    const { date, month, disabledIntervals, minDate, maxDate, mode, className, selectionStart, selectionEnd, highlightedStart, highlightedEnd, onChange, onMouseEnter, ...restProps } = props;
    const cx = reactUtils.useStyles(Calendar_module.default);
    const classNames = cx(className, 'week');
    const today = startOfDay.startOfDay(new Date());
    const start = startOfWeek.startOfWeek(date, { weekStartsOn: 1 });
    const end = endOfWeek.endOfWeek(date, { weekStartsOn: 1 });
    const isBetween = (date, min, max) => date &&
        min &&
        max &&
        (isAfter.isAfter(startOfDay.startOfDay(date), startOfDay.startOfDay(min)) || isEqual.isEqual(startOfDay.startOfDay(date), startOfDay.startOfDay(min))) &&
        (isBefore.isBefore(startOfDay.startOfDay(date), startOfDay.startOfDay(max)) || isEqual.isEqual(startOfDay.startOfDay(date), startOfDay.startOfDay(max)));
    const isDisabledDate = React__namespace.useCallback((date) => {
        let dateDisabled = false;
        if (!disabledIntervals)
            return false;
        for (let i = 0; i < disabledIntervals.length; i++) {
            const { from, to } = disabledIntervals[i];
            dateDisabled = isWithinInterval.isWithinInterval(startOfDay.startOfDay(date), {
                start: startOfDay.startOfDay(from),
                end: startOfDay.startOfDay(to),
            });
            if (dateDisabled) {
                return dateDisabled;
            }
        }
        return false;
    }, [disabledIntervals]);
    const isSelectableDate = React__namespace.useCallback((date) => {
        if (isDisabledDate(date)) {
            return false;
        }
        if (minDate && maxDate) {
            return isWithinInterval.isWithinInterval(date, { start: minDate, end: maxDate });
        }
        if (minDate && !maxDate) {
            return isAfter.isAfter(date, minDate) || isEqual.isEqual(date, minDate);
        }
        if (maxDate && !minDate) {
            return isBefore.isBefore(date, maxDate) || isEqual.isEqual(date, maxDate);
        }
        return true;
    }, [maxDate, minDate, isDisabledDate]);
    const isSelectedDate = React__namespace.useCallback((date) => {
        if (!selectionStart || !selectionEnd)
            return false;
        if (mode === 'range') {
            if (selectionStart && selectionEnd) {
                return isWithinInterval.isWithinInterval(startOfDay.startOfDay(date), {
                    start: startOfDay.startOfDay(selectionStart),
                    end: startOfDay.startOfDay(selectionEnd),
                });
            }
            return false;
        }
        return isEqual.isEqual(date, selectionStart);
    }, [mode, selectionStart, selectionEnd]);
    const isHighlightedDate = React__namespace.useCallback((date) => highlightedStart &&
        highlightedEnd &&
        (isAfter.isAfter(startOfDay.startOfDay(date), startOfDay.startOfDay(highlightedStart)) ||
            isEqual.isEqual(startOfDay.startOfDay(date), startOfDay.startOfDay(highlightedStart))) &&
        (isBefore.isBefore(startOfDay.startOfDay(date), startOfDay.startOfDay(highlightedEnd)) || isEqual.isEqual(startOfDay.startOfDay(date), startOfDay.startOfDay(highlightedEnd))), [highlightedStart, highlightedEnd]);
    const renderDays = React__namespace.useMemo(() => eachDayOfInterval.eachDayOfInterval({ start, end }).map((day) => {
        const prevWeekDate = dateFns.addDays(day, -7);
        const nextWeekDate = dateFns.addDays(day, 7);
        const selectionPrevDaysCount = selectionStart && selectionEnd && isBetween(prevWeekDate, selectionStart, selectionEnd)
            ? differenceInCalendarDays.differenceInCalendarDays(selectionEnd, selectionStart)
            : 0;
        const selectionNextDaysCount = selectionStart && selectionEnd && isBetween(nextWeekDate, selectionStart, selectionEnd)
            ? differenceInCalendarDays.differenceInCalendarDays(selectionEnd, selectionStart)
            : 0;
        const highlightedPrevDaysCount = highlightedStart && highlightedEnd && isBetween(prevWeekDate, highlightedStart, highlightedEnd)
            ? differenceInCalendarDays.differenceInCalendarDays(highlightedEnd, highlightedStart)
            : 0;
        const highlightedNextDaysCount = highlightedStart && highlightedEnd && isBetween(nextWeekDate, highlightedStart, highlightedEnd)
            ? differenceInCalendarDays.differenceInCalendarDays(highlightedEnd, highlightedStart)
            : 0;
        const active = isSelectedDate(day);
        const selectable = isSelectableDate(day);
        const disabled = isDisabledDate(day) || !selectable;
        const isFirstDay = highlightedStart
            ? highlightedStart && isEqual.isEqual(day, highlightedStart)
            : selectionStart && isEqual.isEqual(day, selectionStart);
        const isLastDay = highlightedEnd
            ? highlightedEnd && isEqual.isEqual(day, highlightedEnd)
            : selectionEnd && isEqual.isEqual(day, selectionEnd);
        const isBetweenDay = (selectionStart && selectionEnd && isBetween(day, selectionStart, selectionEnd)) ||
            (highlightedStart && highlightedEnd && isBetween(day, highlightedStart, highlightedEnd));
        const monday = isMonday.isMonday(day);
        const sunday = isSunday.isSunday(day);
        const dayClassNames = cx({
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'without-top-left-border-radius': isBetweenDay && ((!isFirstDay && !monday) || selectionPrevDaysCount >= 7 || highlightedPrevDaysCount >= 7),
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'without-bottom-left-border-radius': isBetweenDay && ((!isFirstDay && !monday) || selectionNextDaysCount >= 7 || highlightedNextDaysCount >= 7),
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'without-top-right-border-radius': isBetweenDay && ((!isLastDay && !sunday) || selectionPrevDaysCount >= 7 || highlightedPrevDaysCount >= 7),
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'without-bottom-right-border-radius': isBetweenDay && ((!isLastDay && !sunday) || selectionNextDaysCount >= 7 || highlightedNextDaysCount >= 7),
        });
        return (React__namespace.createElement(Day.Day, { className: dayClassNames, key: day.getTime(), date: day, active: active, disabled: disabled, highlighted: isHighlightedDate(day), today: isEqual.isEqual(day, today), onChange: onChange, onMouseEnter: !disabled ? onMouseEnter : reactUtils.noop }));
    }), [
        end,
        today,
        start,
        selectionEnd,
        selectionStart,
        highlightedStart,
        highlightedEnd,
        isDisabledDate,
        isHighlightedDate,
        isSelectableDate,
        isSelectedDate,
        onChange,
        onMouseEnter,
        cx,
    ]);
    return (React__namespace.createElement("div", null,
        React__namespace.createElement(Space.Space, { size: 0, align: "center", justify: "center", className: classNames, ...restProps }, renderDays)));
};
Week.displayName = 'Week';

exports.Week = Week;
//# sourceMappingURL=Week.js.map
